/**
 * The public-facing rollout roadmap (see docs/roadmap.md §2 for the full
 * engineering version). Status per phase is derived live from useNodeInfo()
 * flags rather than hardcoded, so this stays accurate as flags flip — no
 * separate "update the marketing copy" step when a phase ships.
 *
 * Consumed by MapPage (full list, now the Map tab of /node-account),
 * LegacyRoadmapPage (the flattened archive at /legacy_roadmap) and
 * WhatsNewRibbon (current/next only). The ribbon is gated behind
 * featureRoadmapHints — see useNodeInfo(). The map is not gated at all: it
 * reports which features are on, and is superadmin-only by virtue of living
 * in node settings. /legacy_roadmap is simply unlinked rather than flag-gated.
 */

export const ROADMAP_PHASES = [
    {
        id: 'people',
        title: 'People',
        description: 'Find the people you know and keep them all in one place.',
        group: 'Entity types',
        // The backend always forces 'person' into enabled_entity_types, so in
        // practice this never reads false — kept as a real check rather than a
        // hardcoded true so it stays honest if that ever changes.
        isLive: (n) => !n.enabledEntityTypes || n.enabledEntityTypes.includes('person'),
    },
    {
        id: 'messaging',
        title: 'Messaging',
        description: 'Send direct messages to anyone on this node.',
        group: 'Communication',
        isLive: (n) => n.featureMessaging,
    },
    {
        id: 'hangouts',
        title: 'Hangouts',
        description: 'Hop on a live video call with someone.',
        group: 'Communication',
        isLive: (n) => n.featureHangouts,
    },
    {
        id: 'schedule',
        title: 'Scheduling',
        description: 'Plan hangouts ahead and see them on a shared calendar.',
        group: 'Communication',
        // A sub-flag of feature_hangouts, but /info already folds that in, so
        // checking this one alone is enough (see use_node_info.js).
        isLive: (n) => n.featureHangoutSchedule,
    },
    {
        id: 'context',
        title: 'Contexts',
        description: 'Organize the people you know into circles — work, family, communities.',
        group: 'Entity types',
        isLive: (n) => !n.enabledEntityTypes || n.enabledEntityTypes.includes('context'),
    },
    {
        id: 'relationship',
        title: 'Relationships',
        description: 'Form groups out of the people and contexts you have.',
        group: 'Entity types',
        isLive: (n) => !n.enabledEntityTypes || n.enabledEntityTypes.includes('relationship'),
    },
    {
        id: 'app',
        title: 'Apps',
        description: 'Channels, rooms and pages — and apps others build — installed in the entities that want them. Nothing is installed by default.',
        group: 'Entity types',
        isLive: (n) => !n.enabledEntityTypes || n.enabledEntityTypes.includes('app'),
    },
    {
        id: 'byoagent',
        title: 'Bring your own agent',
        description: 'Use the AI you already have — Claude, ChatGPT, a script — through this node\'s MCP server, with an agent token you mint, scope and revoke in Account settings.',
        group: 'AI agents',
        isLive: () => true,
    },
    {
        id: 'schedules',
        title: 'Scheduled insights',
        description: 'An app a context installs runs searches on a schedule and surfaces short-lived insights from across the node.',
        group: 'AI agents',
        // No flag yet — always "coming" until it ships, as an app a context
        // installs (docs/october-model-todos.md 3.11).
        isLive: () => false,
    },
    {
        id: 'federation',
        title: 'Federation',
        description: 'Connect, message, and call across Vellby nodes, not just within one.',
        group: 'Across nodes',
        isLive: (n) => n.federationEnabled,
    },
    {
        id: 'facilitator',
        title: 'AI-facilitated breakouts',
        description: 'An AI agent splits a hangout into breakout rooms and lightly facilitates each one.',
        group: 'AI agents',
        isLive: (n) => n.featureHangoutFacilitator,
    },
    {
        id: 'sidebyside',
        title: 'Side-by-siding',
        description: 'Put two entities next to each other: both profiles, what connects them, and how each shows itself in the places they share — for you to read, or for your own agent to compare.',
        group: 'AI agents',
        // See docs/side-by-siding.md. Same-node, two entities; no model on the node.
        isLive: () => true,
    },
    {
        id: 'regrouper',
        title: 'Regrouper',
        description: 'Remix people into new relationships against a criterion — balanced on something like gender or industry, or deliberately split along it — each run seeded differently, so a regrouping is an experiment you can re-roll.',
        group: 'AI agents',
        // No flag yet — always "coming" until it ships, and last in the array
        // so it doesn't displace whatever the what's-new ribbon currently
        // teases. It ships as an app a context installs
        // (docs/october-model-todos.md 3.11), not behind a node flag.
        isLive: () => false,
    },
];

export function getRoadmapPhases(nodeInfo) {
    return ROADMAP_PHASES.map(phase => ({ ...phase, live: !!phase.isLive(nodeInfo) }));
}

// First phase, in order, that isn't live yet — the "next up" teaser.
export function getNextPhase(nodeInfo) {
    return getRoadmapPhases(nodeInfo).find(phase => !phase.live) || null;
}

// Display order of the groups on the /map page. The array order above is a
// rollout sequence; this grouping deliberately is not one — the map answers
// "what does this node do", which has no first or last.
export const CAPABILITY_GROUPS = ['Communication', 'Entity types', 'AI agents', 'Across nodes'];

// The same phases, bucketed by group, for the /map capability blocks. Empty
// groups are dropped so adding a group name here costs nothing until a phase
// actually claims it.
export function getCapabilityGroups(nodeInfo) {
    const phases = getRoadmapPhases(nodeInfo);
    return CAPABILITY_GROUPS
        .map(name => ({ name, blocks: phases.filter(phase => phase.group === name) }))
        .filter(group => group.blocks.length > 0);
}
